var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement, memo, useState, useEffect, useContext } from '@max/max';
import { useSelector, useDispatch } from '@max/max-redux';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import Image from '@hfe/max-image';
import { Actions } from '../../store/index';
import { getCalendarModel, getHolidaysModel, myDayjs } from '../../utils/CalendarUtils';
import { DateShortcutType } from './types';
import LoadingView from '@max/leez-loading-view';
import CalendarHeader from './SubComponents/CalendarHeader';
import CalendarFooter from './SubComponents/CalendarFooter';
import CalendarBody from './SubComponents/CalendarBody';
import SlideModal from '@max/leez-slide-modal';
import dayjs from 'dayjs';
import './calendar.scss';
import { getInitCheckDate } from '../../utils/getInitCheckDate';
/**
 * 赋予一个默认值，初始化时默认选中今天和明天
 */
const findDefaultDateRange = calendarModel => {
    var _a;
    let startDate;
    let endDate;
    for (let i = 0; i < (calendarModel === null || calendarModel === void 0 ? void 0 : calendarModel.length); i++) {
        const days = ((_a = calendarModel[i]) === null || _a === void 0 ? void 0 : _a.values) || [];
        const length = (days === null || days === void 0 ? void 0 : days.length) || 0;
        for (let j = 0; j < length; j++) {
            if (days[j].shortcutType === DateShortcutType.today) {
                startDate = days[j];
            }
            else if (days[j].shortcutType === DateShortcutType.tommorrow) {
                endDate = days[j];
                break;
            }
        }
        if (startDate && endDate) {
            break;
        }
    }
    return {
        startDate,
        endDate
    };
};
let lastTimeState;
function Calendar(props) {
    const dispatch = useDispatch();
    const isShowCalendarPanel = useSelector(state => state.calendar.isShowCalendarPanel);
    const dateTimeRange = useSelector(state => ({
        startDate: state.calendar.startDate,
        endDate: state.calendar.endDate
    }));
    // @ts-expect-error
    const p = useContext(global._EngineGlobalContext);
    const [calendarState, setCalendarState] = useState({});
    const [holidayState, setHolidayState] = useState({});
    const [isLoading, setLoadingState] = useState(true);
    const [viewState, setViewState] = useState({
        startDay: null,
        startDayDesc: '',
        endDay: null,
        endDayDesc: '',
        intervalDayNum: 0,
        isBeforeDawn: false
    });
    /**
     * 数据初始化
     * 1.获取日历模型
     * 2.请求节假日信息
     * 3.赋予初始值
     */
    const initData = () => __awaiter(this, void 0, void 0, function* () {
        let isBeforeDawn = false;
        // 判断是否是凌晨入住
        const current = myDayjs();
        const hour = current.hour();
        const minute = current.minute();
        if (hour >= 0 && hour < 6 || hour === 6 && minute === 0) {
            isBeforeDawn = true;
            dispatch({
                type: Actions.getIsBeforeDawn,
                data: {
                    isBeforeDawn: true
                }
            });
        }
        const calendarModel = getCalendarModel(isBeforeDawn);
        setCalendarState(calendarModel);
        let { startDay: startDate, endDay: endDate } = viewState;
        const { checkIn, checkOut } = yield getInitCheckDate(p, isBeforeDawn, current, props);
        const { startDate: defaultStartDate, endDate: defaultEndDate } = findDefaultDateRange(calendarModel);
        if (!startDate || !endDate || (startDate === null || startDate === void 0 ? void 0 : startDate.disabled) || (endDate === null || endDate === void 0 ? void 0 : endDate.disabled)) {
            startDate = checkIn || dateTimeRange.startDate || defaultStartDate;
            endDate = checkOut || dateTimeRange.endDate || defaultEndDate;
            dispatch({
                type: Actions.UpdateDateRange,
                data: {
                    startDate,
                    endDate
                }
            });
        }
        // console.log('final startDate', startDate, 'final endDate', endDate);
        // 是否要请求假期数据
        if ((lastTimeState === null || lastTimeState === void 0 ? void 0 : lastTimeState.indexKey) !== (defaultStartDate === null || defaultStartDate === void 0 ? void 0 : defaultStartDate.indexKey)) {
            try {
                const holidays = yield getHolidaysModel(calendarModel);
                setHolidayState(holidays);
            }
            catch (err) {
                console.log(err);
            }
            lastTimeState = defaultStartDate;
        }
        setViewState(Object.assign(Object.assign({}, viewState), { startDay: startDate, endDay: endDate, intervalDayNum: -dayjs(startDate.value).diff(dayjs(endDate.value), 'days'), isBeforeDawn }));
        setLoadingState(false);
    });
    // 同步选择
    const syncSelection = value => {
        const { startDay, endDay } = viewState;
        const temporaryValue = {
            startDay: viewState.startDay,
            endDay: viewState.endDay,
            intervalDayNum: 0
        };
        if (!startDay) {
            temporaryValue.startDay = value;
        }
        else if (!endDay) {
            // 判断日期是不是在开始日期之前
            const diffWithStartDay = dayjs(value.value).diff(dayjs(startDay.value), 'days');
            if (diffWithStartDay === 0) {
                return;
            }
            if (diffWithStartDay < 0) {
                temporaryValue.startDay = value;
                temporaryValue.endDay = null;
            }
            else {
                temporaryValue.endDay = value;
            }
        }
        else {
            temporaryValue.startDay = value;
            temporaryValue.endDay = null;
        }
        if (temporaryValue.startDay && temporaryValue.endDay) {
            temporaryValue.intervalDayNum = -dayjs(temporaryValue.startDay.value).diff(dayjs(temporaryValue.endDay.value), 'days');
        }
        setViewState(Object.assign(Object.assign({}, viewState), temporaryValue));
    };
    const hide = () => {
        dispatch({
            type: Actions.toggleCalendarPanel,
            data: {
                isShow: false
            }
        });
    };
    const confirm = () => {
        dispatch({
            type: Actions.UpdateDateRange,
            data: {
                startDate: viewState.startDay,
                endDate: viewState.endDay
            }
        });
        hide();
    };
    useEffect(() => {
        initData();
    }, []);
    useEffect(() => {
        // console.log('isShowCalendarPanel', isShowCalendarPanel, 'screenHeight', screenHeight);
        if (isShowCalendarPanel) {
            initData();
        }
        const { enableScroll, disableScroll } = p._pageAPI || {};
        if (disableScroll && enableScroll) {
            isShowCalendarPanel ? disableScroll() : enableScroll();
        }
    }, [isShowCalendarPanel]);
    return <SlideModal visible={isShowCalendarPanel} showHeader={false} showCloseBtn={false} maskClosable addBottomSpace={false} contentStyle={{
        margin: 0,
        paddingLeft: 0,
        paddingRight: 0,
        padding: 0
    }} onClosePress={hide}>

      <View className="calendar-modal-header-container">
        <Text className="calendar-modal-header--title">选择日期</Text>
        <View>
          <Image className="calendar-modal-header--close-img" source={{
        uri: 'https://p0.meituan.net/ingee/3480c55c62f6098dca8c26bae72c88f84476.png'
    }} style={{
        pointerEvents: 'auto'
    }} onClick={hide}/>
        </View>
      </View>
      <LoadingView x-if={isLoading} className="calendar-modal-loading-container"/>
      <View x-else className="calendar-modal-body-container">
        <CalendarHeader />
        <CalendarBody calendarState={calendarState} syncSelection={syncSelection} viewState={viewState} holidayState={holidayState} isVisible={isShowCalendarPanel}/>
        <CalendarFooter viewState={viewState} onConfirm={confirm}/>
      </View>
    </SlideModal>;
}
export default memo(Calendar);
